import React, { useState, useEffect, useRef, useCallback } from "react";
import "./AntiSpam.css";

/**
 * AntiSpam – Schutz vor Bot-Anfragen ohne externe API oder Library.
 *
 * Drei Schichten:
 *   1. Honeypot (unsichtbar): Ein verstecktes Feld, das nur Bots ausfüllen.
 *   2. Zeit-Trap (unsichtbar): Absenden < MIN_TIME_MS nach Load wird verworfen.
 *   3. Sicherheits-Modal (sichtbar): Kleine Rechenaufgabe nach Button-Klick,
 *      mit freundlicher Erklärung – damit der Nutzer nicht verwirrt ist.
 *
 * Nutzung in einem Formular:
 *
 *   const antiSpam = useAntiSpam();
 *
 *   const onSubmit = (e) => {
 *     e.preventDefault();
 *     // ... eigene Feldvalidierung ...
 *     antiSpam.requestSubmit(() => {
 *       // wird nach erfolgreicher Sicherheitsprüfung ausgeführt
 *       actualSubmit();
 *     });
 *   };
 *
 *   return (
 *     <form onSubmit={onSubmit}>
 *       ...
 *       <AntiSpamHoneypot {...antiSpam} />
 *       <button type="submit">Absenden</button>
 *       <AntiSpamModal {...antiSpam} />
 *     </form>
 *   );
 */

const MIN_TIME_MS = 3000; // Formular muss min. 3 Sek. offen sein

function generateChallenge() {
  const a = Math.floor(Math.random() * 9) + 1; // 1-9
  const b = Math.floor(Math.random() * 9) + 1; // 1-9
  return { a, b, answer: a + b };
}

export function useAntiSpam() {
  const [challenge, setChallenge] = useState(generateChallenge);
  const [captchaInput, setCaptchaInput] = useState("");
  const [error, setError] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const openedAtRef = useRef(Date.now());
  const onConfirmRef = useRef(null);

  useEffect(() => {
    openedAtRef.current = Date.now();
  }, []);

  const refresh = useCallback(() => {
    setChallenge(generateChallenge());
    setCaptchaInput("");
    setError("");
  }, []);

  // Wird vom Formular aufgerufen, wenn der Nutzer "Absenden" geklickt hat.
  // Öffnet das Sicherheits-Modal – oder schluckt Bot-Anfragen still.
  const requestSubmit = useCallback(
    (onConfirm) => {
      // Honeypot: Bot ausgefüllt → still verwerfen
      if (honeypot.trim() !== "") {
        return;
      }
      // Zeit-Trap: zu schnell → still verwerfen
      if (Date.now() - openedAtRef.current < MIN_TIME_MS) {
        return;
      }
      onConfirmRef.current = onConfirm;
      refresh();
      setIsOpen(true);
    },
    [honeypot, refresh]
  );

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setCaptchaInput("");
    setError("");
    onConfirmRef.current = null;
  }, []);

  const confirm = useCallback(() => {
    const given = parseInt(captchaInput, 10);
    if (Number.isNaN(given) || given !== challenge.answer) {
      setError("Das Ergebnis stimmt leider nicht. Bitte versuchen Sie es erneut.");
      refresh();
      return;
    }
    const cb = onConfirmRef.current;
    onConfirmRef.current = null;
    setIsOpen(false);
    setCaptchaInput("");
    setError("");
    if (cb) cb();
  }, [captchaInput, challenge.answer, refresh]);

  const reset = useCallback(() => {
    setHoneypot("");
    setCaptchaInput("");
    setError("");
    setIsOpen(false);
    onConfirmRef.current = null;
    refresh();
    openedAtRef.current = Date.now();
  }, [refresh]);

  return {
    challenge,
    captchaInput,
    setCaptchaInput,
    honeypot,
    setHoneypot,
    error,
    isOpen,
    requestSubmit,
    closeModal,
    confirm,
    refresh,
    reset,
  };
}

/**
 * Unsichtbares Honeypot-Feld – muss innerhalb des <form> gerendert werden.
 */
export function AntiSpamHoneypot({ honeypot, setHoneypot }) {
  return (
    <div className="antispam-honeypot" aria-hidden="true">
      <label htmlFor="website-url">Website (nicht ausfüllen)</label>
      <input
        id="website-url"
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
      />
    </div>
  );
}

/**
 * Sicherheits-Modal mit Math-Captcha und freundlicher Erklärung.
 */
export function AntiSpamModal({
  isOpen,
  challenge,
  captchaInput,
  setCaptchaInput,
  error,
  confirm,
  closeModal,
  refresh,
}) {
  // ESC schließt das Modal, Enter bestätigt
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
      if (e.key === "Enter") {
        e.preventDefault();
        confirm();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, closeModal, confirm]);

  // Body-Scroll sperren, solange Modal offen
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Fokus automatisch auf das Input setzen
  const inputRef = useRef(null);
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="antispam-modal-overlay"
      onClick={closeModal}
      role="presentation"
    >
      <div
        className="antispam-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="antispam-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="antispam-modal-close"
          onClick={closeModal}
          aria-label="Schließen"
        >
          ×
        </button>

        <div className="antispam-modal-icon" aria-hidden="true">
          {/* Schild-Icon inline SVG in Weber-Grün */}
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 2L4 5v6c0 5 3.5 9.7 8 11 4.5-1.3 8-6 8-11V5l-8-3z"
              fill="currentColor"
              opacity="0.15"
            />
            <path
              d="M12 2L4 5v6c0 5 3.5 9.7 8 11 4.5-1.3 8-6 8-11V5l-8-3z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
            <path
              d="M8.5 12l2.5 2.5L15.5 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h3 id="antispam-modal-title" className="antispam-modal-title">
          Kurze Sicherheitsprüfung
        </h3>
        <p className="antispam-modal-text">
          Damit wir sicher gehen können, dass Ihre Anfrage von einem Menschen
          stammt, bitten wir Sie diese kurze Rechenaufgabe zu lösen. Vielen
          Dank!
        </p>

        <div className="antispam-modal-challenge">
          <span className="antispam-modal-question">
            {challenge.a} <span className="antispam-modal-op">+</span>{" "}
            {challenge.b} <span className="antispam-modal-op">=</span>
          </span>
          <input
            ref={inputRef}
            className={`antispam-modal-input ${error ? "has-error" : ""}`}
            type="text"
            inputMode="numeric"
            value={captchaInput}
            onChange={(e) => setCaptchaInput(e.target.value)}
            placeholder="?"
            autoComplete="off"
          />
          <button
            type="button"
            className="antispam-modal-refresh"
            onClick={refresh}
            aria-label="Neue Aufgabe"
            title="Neue Aufgabe"
          >
            ↻
          </button>
        </div>

        {error && <div className="antispam-modal-error">{error}</div>}

        <div className="antispam-modal-actions">
          <button
            type="button"
            className="antispam-modal-btn antispam-modal-btn-secondary"
            onClick={closeModal}
          >
            Abbrechen
          </button>
          <button
            type="button"
            className="antispam-modal-btn antispam-modal-btn-primary"
            onClick={confirm}
          >
            Bestätigen &amp; Absenden
          </button>
        </div>
      </div>
    </div>
  );
}
